import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, Shield, User, KeyRound, Trash2, Pencil, Plus,
  FolderKanban, X, Wallet, Power, Loader2, Eye, EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import { teamAPI } from '../../api';

const err = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').join(' ');
  return fallback;
};

const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600" data-testid="modal-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const RoleBadge = ({ role }) => (
  role === 'sub_admin' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
      <Shield className="w-3 h-3" /> Sub-Admin
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
      <User className="w-3 h-3" /> User
    </span>
  )
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const PasswordInput = ({ value, onChange, testId, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder || 'Min 8 chars, 1 uppercase, 1 lowercase, 1 digit'}
        className={inputCls}
        data-testid={testId}
      />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2.5 text-slate-400">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const emptyMemberForm = {
  name: '', email: '', phone: '', password: '', role: 'user', group_id: '', budget_limit: ''
};

export default function Team() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [teamLimit, setTeamLimit] = useState(1);
  const [actingRole, setActingRole] = useState('owner');
  const [tab, setTab] = useState('members');

  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwdUser, setPwdUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyMemberForm);
  const [newPassword, setNewPassword] = useState('');

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });

  const isOwner = actingRole === 'owner';

  const fetchAll = useCallback(async () => {
    try {
      const [usersRes, groupsRes] = await Promise.all([
        teamAPI.listUsers(),
        teamAPI.listGroups()
      ]);
      setUsers(usersRes.data.users || []);
      setTeamLimit(usersRes.data.team_limit);
      setActingRole(usersRes.data.acting_role || 'owner');
      setGroups(groupsRes.data.groups || []);
    } catch (e) {
      toast.error(err(e, 'Failed to load team'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => { setForm(emptyMemberForm); setShowAdd(true); };
  const openEdit = (u) => {
    setForm({
      name: u.name, email: u.email, phone: u.phone || '', password: '',
      role: u.role, group_id: u.group_id || '',
      budget_limit: u.budget_limit != null ? String(u.budget_limit) : ''
    });
    setEditUser(u);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, password: form.password, role: form.role,
      };
      if (form.phone) payload.phone = form.phone;
      if (isOwner && form.group_id) payload.group_id = form.group_id;
      if (isOwner && form.budget_limit) payload.budget_limit = parseFloat(form.budget_limit);
      await teamAPI.createUser(payload);
      toast.success('Team member created');
      setShowAdd(false);
      fetchAll();
    } catch (e2) {
      toast.error(err(e2, 'Failed to create member'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name };
      if (form.phone) payload.phone = form.phone;
      if (isOwner) {
        payload.role = form.role;
        if (form.group_id) payload.group_id = form.group_id;
        else payload.clear_group = true;
        if (form.budget_limit) payload.budget_limit = parseFloat(form.budget_limit);
        else payload.clear_budget = true;
      }
      await teamAPI.updateUser(editUser.user_id, payload);
      toast.success('Member updated');
      setEditUser(null);
      fetchAll();
    } catch (e2) {
      toast.error(err(e2, 'Failed to update member'));
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await teamAPI.setPassword(pwdUser.user_id, { new_password: newPassword });
      toast.success(`Password updated for ${pwdUser.name}`);
      setPwdUser(null);
      setNewPassword('');
    } catch (e2) {
      toast.error(err(e2, 'Failed to update password'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (u) => {
    const next = u.status === 'active' ? 'disabled' : 'active';
    try {
      await teamAPI.setStatus(u.user_id, { status: next });
      toast.success(next === 'active' ? `${u.name} enabled` : `${u.name} disabled`);
      fetchAll();
    } catch (e2) {
      toast.error(err(e2, 'Failed to update status'));
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await teamAPI.deleteUser(deleteTarget.user_id);
      toast.success('Member removed');
      setDeleteTarget(null);
      fetchAll();
    } catch (e2) {
      toast.error(err(e2, 'Failed to remove member'));
    } finally {
      setSaving(false);
    }
  };

  const handleGroupSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editGroup) {
        await teamAPI.updateGroup(editGroup.group_id, groupForm);
        toast.success('Group updated');
      } else {
        await teamAPI.createGroup(groupForm);
        toast.success('Group created');
      }
      setShowGroupModal(false);
      setEditGroup(null);
      fetchAll();
    } catch (e2) {
      toast.error(err(e2, 'Failed to save group'));
    } finally {
      setSaving(false);
    }
  };

  const handleGroupDelete = async (g) => {
    try {
      await teamAPI.deleteGroup(g.group_id);
      toast.success('Group deleted');
      fetchAll();
    } catch (e2) {
      toast.error(err(e2, 'Failed to delete group'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const limitLabel = teamLimit === -1 ? 'Unlimited' : `${users.length} / ${teamLimit}`;
  const atLimit = teamLimit !== -1 && users.length >= teamLimit;

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" /> Team Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isOwner
              ? 'Create sub-admins and users with their own logins. They share your usage balance.'
              : 'Manage the users in your group.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-lg" data-testid="team-limit-label">
            Members: {limitLabel}
          </span>
          <button
            onClick={openAdd}
            disabled={atLimit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-member-btn"
            title={atLimit ? 'Team member limit reached for your plan' : ''}
          >
            <UserPlus className="w-4 h-4" /> Add Member
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setTab('members')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'members' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
          data-testid="members-tab"
        >
          Members
        </button>
        <button
          onClick={() => setTab('groups')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'groups' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
          data-testid="groups-tab"
        >
          Groups
        </button>
      </div>

      {tab === 'members' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {users.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="team-empty-state">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No team members yet. Add your first sub-admin or user.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="px-4 py-3">Member</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Group</th>
                    <th className="px-4 py-3">Daily Budget</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`team-row-${u.email}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{u.name}</p>
                        <p className="text-xs text-slate-400">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
                      </td>
                      <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                      <td className="px-4 py-3 text-slate-600">{u.group_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3">
                        {u.budget_limit != null ? (
                          <div className="text-xs">
                            <span className="font-medium text-slate-800">₹{u.budget_limit.toLocaleString()}/day</span>
                            <span className="text-slate-400"> · used today ₹{u.budget_used.toLocaleString()}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Shared balance</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleStatus(u)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            u.status === 'active' ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-red-50 text-red-600 hover:bg-red-100'
                          }`}
                          data-testid={`status-toggle-${u.email}`}
                          title="Click to toggle access"
                        >
                          <Power className="w-3 h-3" />
                          {u.status === 'active' ? 'Active' : 'Disabled'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(u)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit" data-testid={`edit-member-${u.email}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setPwdUser(u); setNewPassword(''); }} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title="Change password" data-testid={`password-member-${u.email}`}>
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteTarget(u)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Remove" data-testid={`delete-member-${u.email}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'groups' && (
        <div className="space-y-4">
          {isOwner && (
            <button
              onClick={() => { setEditGroup(null); setGroupForm({ name: '', description: '' }); setShowGroupModal(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-700"
              data-testid="add-group-btn"
            >
              <Plus className="w-4 h-4" /> New Group
            </button>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.length === 0 && (
              <div className="col-span-full p-10 text-center text-slate-400 bg-white rounded-xl border border-slate-200" data-testid="groups-empty-state">
                <FolderKanban className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No groups yet. Groups help you organise members by store or department.</p>
              </div>
            )}
            {groups.map((g) => (
              <div key={g.group_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`group-card-${g.name}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{g.name}</p>
                    {g.description && <p className="text-xs text-slate-400 mt-0.5">{g.description}</p>}
                    <p className="text-xs text-slate-500 mt-2">{g.member_count} member{g.member_count !== 1 ? 's' : ''}</p>
                  </div>
                  {isOwner && (
                    <div className="flex gap-1">
                      <button onClick={() => { setEditGroup(g); setGroupForm({ name: g.name, description: g.description || '' }); setShowGroupModal(true); }} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`edit-group-${g.name}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleGroupDelete(g)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`delete-group-${g.name}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add / Edit member modal */}
      {(showAdd || editUser) && (
        <Modal
          title={editUser ? `Edit ${editUser.name}` : 'Add Team Member'}
          onClose={() => { setShowAdd(false); setEditUser(null); }}
          testId="member-modal"
        >
          <form onSubmit={editUser ? handleUpdate : handleCreate} className="space-y-4">
            <Field label="Full Name">
              <input className={inputCls} value={form.name} required minLength={2}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name-input" />
            </Field>
            <Field label="Email">
              <input className={`${inputCls} ${editUser ? 'bg-slate-50 text-slate-400' : ''}`} type="email" value={form.email} required disabled={!!editUser}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email-input" />
            </Field>
            <Field label="Phone (optional — enables OTP login)">
              <input className={inputCls} value={form.phone} placeholder="10-digit mobile number"
                onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="member-phone-input" />
            </Field>
            {!editUser && (
              <Field label="Password">
                <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="member-password-input" />
              </Field>
            )}
            <Field label="Role">
              <select
                className={`${inputCls} ${!isOwner ? 'bg-slate-50 text-slate-400' : ''}`}
                value={isOwner ? form.role : 'user'}
                disabled={!isOwner}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="member-role-select"
              >
                <option value="user">User</option>
                {isOwner && <option value="sub_admin">Sub-Admin (can create users in their group)</option>}
              </select>
            </Field>
            {isOwner ? (
              <Field label="Group (optional)">
                <select className={inputCls} value={form.group_id}
                  onChange={(e) => setForm({ ...form, group_id: e.target.value })} data-testid="member-group-select">
                  <option value="">No group</option>
                  {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                </select>
              </Field>
            ) : (
              <p className="text-xs text-slate-400">New users are automatically added to your group.</p>
            )}
            {isOwner && (
              <Field label="Daily Budget Limit (₹, optional)">
                <div className="relative">
                  <Wallet className="w-4 h-4 text-slate-300 absolute left-3 top-2.5" />
                  <input className={`${inputCls} pl-9`} type="number" min="1" step="any" value={form.budget_limit}
                    placeholder="₹ per day · leave empty to share the account balance"
                    onChange={(e) => setForm({ ...form, budget_limit: e.target.value })} data-testid="member-budget-input" />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Resets every day at 12:00 AM IST.</p>
              </Field>
            )}
            <button type="submit" disabled={saving}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="member-submit-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editUser ? 'Save Changes' : 'Create Member'}
            </button>
          </form>
        </Modal>
      )}

      {/* Change password modal */}
      {pwdUser && (
        <Modal title={`Set password for ${pwdUser.name}`} onClose={() => setPwdUser(null)} testId="password-modal">
          <form onSubmit={handleSetPassword} className="space-y-4">
            <Field label="New Password">
              <PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} testId="new-password-input" />
            </Field>
            <button type="submit" disabled={saving || newPassword.length < 8}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="password-submit-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Update Password
            </button>
          </form>
        </Modal>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <Modal title="Remove team member?" onClose={() => setDeleteTarget(null)} testId="delete-modal">
          <p className="text-sm text-slate-600 mb-4">
            <strong>{deleteTarget.name}</strong> ({deleteTarget.email}) will lose access immediately. This cannot be undone.
          </p>
          <div className="flex gap-3">
            <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={handleDelete} disabled={saving}
              className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              data-testid="delete-confirm-btn">
              Remove
            </button>
          </div>
        </Modal>
      )}

      {/* Group modal */}
      {showGroupModal && (
        <Modal title={editGroup ? 'Edit Group' : 'New Group'} onClose={() => { setShowGroupModal(false); setEditGroup(null); }} testId="group-modal">
          <form onSubmit={handleGroupSave} className="space-y-4">
            <Field label="Group Name">
              <input className={inputCls} value={groupForm.name} required minLength={2} placeholder="e.g. Store — Koramangala"
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} data-testid="group-name-input" />
            </Field>
            <Field label="Description (optional)">
              <input className={inputCls} value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} data-testid="group-description-input" />
            </Field>
            <button type="submit" disabled={saving}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              data-testid="group-submit-btn">
              {editGroup ? 'Save Changes' : 'Create Group'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
